import React from "react";
import { useTranslation } from "react-i18next";

/**
 * What Oliver sent, exactly as it sent it.
 *
 * Read-only and deliberately plain: this is the evidence the draft beside it
 * was built from. It also shows what the draft does not carry — the receiver,
 * the carrier, the final destination, and the container drop-off depot, which
 * is received but never routed — so nothing on the document is out of sight
 * while the order is being decided.
 */

const val = (v: unknown) => {
  if (v === true) return "✓";
  if (v === false) return "—";
  const s = typeof v === "string" ? v.trim() : v == null ? "" : String(v);
  return s || "—";
};

const Row: React.FC<{ label: string; value: unknown }> = ({ label, value }) => (
  <div className="grid grid-cols-[110px_1fr] gap-2 py-0.5">
    <div className="text-[#8A9590]">{label}</div>
    <div className="text-[#0B0F0E] break-words">{val(value)}</div>
  </div>
);

const Block: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="py-2 border-t border-[#EFEDE7] first:border-t-0">
    <div className="text-[11px] font-semibold uppercase tracking-wide text-[#8A9590] mb-1">
      {title}
    </div>
    {children}
  </div>
);

const hasAny = (obj: Record<string, unknown> | undefined) =>
  !!obj && Object.values(obj).some((v) => typeof v === "string" && v.trim());

const Location: React.FC<{ title: string; loc?: Record<string, any> }> = ({ title, loc }) => {
  const { t } = useTranslation("containerOrderIntake");
  if (!hasAny(loc)) {
    return (
      <Block title={title}>
        <div className="text-[#8A9590]">{t("source.notStated")}</div>
      </Block>
    );
  }
  return (
    <Block title={title}>
      <Row label={t("source.company")} value={loc!.company} />
      <Row
        label={t("source.address")}
        value={[loc!.street, loc!.city, loc!.county, loc!.countryCode].filter(Boolean).join(", ")}
      />
      {loc!.reference !== undefined && <Row label={t("source.reference")} value={loc!.reference} />}
      {loc!.date !== undefined && (
        <Row
          label={t("source.when")}
          value={[loc!.date, [loc!.timeFrom, loc!.timeTo].filter(Boolean).join("–")]
            .filter(Boolean)
            .join(" ")}
        />
      )}
      {(loc!.latitude || loc!.longitude) && (
        <Row label={t("source.location")} value={`${loc!.latitude}, ${loc!.longitude}`} />
      )}
    </Block>
  );
};

const Party: React.FC<{ title: string; party?: Record<string, any> }> = ({ title, party }) => {
  const { t } = useTranslation("containerOrderIntake");
  if (!party || !hasAny({ ...party, address: undefined })) return null;
  const a = party.address || {};
  return (
    <Block title={title}>
      <Row label={t("source.company")} value={party.name} />
      <Row label={t("source.regCode")} value={party.registrationCode} />
      {party.vatNumber !== undefined && <Row label={t("source.vat")} value={party.vatNumber} />}
      <Row label={t("source.email")} value={party.email} />
      <Row label={t("source.phone")} value={party.phone} />
      <Row
        label={t("source.address")}
        value={[a.street, a.postalCode, a.city, a.county, a.country || a.countryCode]
          .filter(Boolean)
          .join(", ")}
      />
    </Block>
  );
};

const IntakeSourcePanel: React.FC<{ raw: Record<string, any> | null }> = ({ raw }) => {
  const { t } = useTranslation("containerOrderIntake");
  if (!raw) return <div className="text-[13px] text-[#8A9590]">{t("source.none")}</div>;

  const locations = raw.shippingLocations || {};

  return (
    <div className="text-[12px]">
      <Block title={t("source.document")}>
        <Row label={t("source.file")} value={raw.originalName} />
        <Row label={t("source.readyId")} value={raw.readyId} />
        <Row label={t("source.booking")} value={raw.bookingNumber} />
        <Row label={t("source.driverOrder")} value={raw.driverOrderNumber} />
      </Block>

      <Party title={t("source.orderingParty")} party={raw.orderingParty} />

      <Location title={t("source.containerPickup")} loc={raw.containerPickup} />
      <Location title={t("source.loading")} loc={locations.loadingLocation} />
      <Location title={t("source.dropOff")} loc={locations.dropOff} />
      {/* Received, and deliberately not part of the draft: returning the empty
          box is its own movement, not a stop on this carriage. */}
      <Location title={t("source.containerDropOff")} loc={raw.containerDropOff} />
      {hasAny(locations.destination) && (
        <Location title={t("source.destination")} loc={locations.destination} />
      )}

      {(raw.containers || []).map((c: any, i: number) => (
        <Block key={i} title={t("source.container", { n: i + 1 })}>
          <Row label={t("source.number")} value={c.containerNumber} />
          <Row label={t("source.type")} value={c.containerType} />
          <Row label={t("source.opt")} value={c.opt} />
          <Row label={t("source.truck")} value={c.truck?.nr} />
          <Row label={t("source.plugin")} value={c.truck?.plugin} />
          <Row label={t("source.genSet")} value={c.truck?.genSet} />
          <Row label={t("source.preCooling")} value={c.truck?.preCooling} />
          <Row label={t("source.dangerous")} value={c.goods?.dangerous} />
          <Row label={t("source.temperature")} value={c.goods?.temperature} />
          {(c.goods?.items || []).map((item: any, j: number) => (
            <Row
              key={j}
              label={t("source.goods", { n: j + 1 })}
              value={`${val(item.quantity)} × ${val(item.name)} · ${val(item.weight)}`}
            />
          ))}
          {Object.entries(c.additional || {}).map(([k, v]) => (
            <Row key={k} label={k} value={v} />
          ))}
        </Block>
      ))}

      <Block title={t("source.instructions")}>
        <Row label={t("source.remarks")} value={raw.remarks} />
        <Row label={t("source.pickupInstructions")} value={raw.pickupInstructions} />
        <Row label={t("source.deliveryInstructions")} value={raw.deliveryInstructions} />
      </Block>

      <Party title={t("source.receiver")} party={raw.receiver} />
      <Party title={t("source.carrier")} party={raw.carrier} />
    </div>
  );
};

export default IntakeSourcePanel;
